import { safeStr } from './helper';

export function onlyDigits(v) {
  return safeStr(v).replace(/\D/g, '');
}

export const southAmericaDDI = [
  '51', // Peru
  '54', // Argentina591
  '55', // Brasil
  '56', // Chile
  '57', // Colômbia
  '58', // Venezuela
  '591', // Bolívia
  '592', // Guiana
  '593', // Equador
  '595', // Paraguai
  '597', // Suriname
  '598', // Uruguai
];

const internationalDocByDDI = {
  '51': {
    rules: [
      { length: 8, mask: '########' }, // DNI
      { length: 11, mask: '###########' }, // RUC
    ],
  },
  '54': {
    rules: [
      { length: 8, mask: '##.###.###' }, // DNI
      { length: 11, mask: '##-########-#' }, // CUIT/CUIL
    ],
  },
  '55': {
    rules: [
      { length: 11, mask: '###.###.###-##' }, // CPF
      { length: 14, mask: '##.###.###/####-##' }, // CNPJ
    ],
  },
  '56': {
    rules: [{ length: 9, mask: '##.###.###-#' }], // RUT
  },
  '57': {
    rules: [
      { length: 9, mask: '###.###.###' }, // CC
      { length: 10, mask: '###.###.###-#' }, // NIT
    ],
  },
  '58': {
    rules: [
      { length: 8, mask: '##.###.###' }, // CI
      { length: 10, mask: '##.###.###-#' }, // RIF
    ],
  },
  '591': {
    rules: [
      { length: 8, mask: '##.###.###' }, // CI
      { length: 10, mask: '##########' }, // NIT
    ],
  },
  '592': {
    rules: [{ length: 9, mask: '#########' }], // ID/TIN
  },
  '593': {
    rules: [
      { length: 10, mask: '##########' }, // Cedula
      { length: 13, mask: '#############' }, // RUC
    ],
  },
  '595': {
    rules: [
      { length: 7, mask: '#.###.###' }, // CI
      { length: 9, mask: '###.###.###' }, // RUC
    ],
  },
  '597': {
    rules: [
      { length: 7, mask: '#######' }, // ID
      { length: 9, mask: '#########' }, // TIN
    ],
  },
  '598': {
    rules: [
      { length: 8, mask: '#.###.###-#' }, // CI
      { length: 12, mask: '############' }, // RUT
    ],
  },
};

function applyMaskPattern(digits, pattern) {
  let out = '';
  let dIdx = 0;

  for (let i = 0; i < pattern.length; i += 1) {
    if (dIdx >= digits.length) break;
    const ch = pattern[i];
    if (ch === '#') {
      out += digits[dIdx];
      dIdx += 1;
    } else {
      out += ch;
    }
  }

  return out;
}

function pickInternationalRule(len, rules) {
  if (!rules?.length) return null;
  const sorted = [...rules].sort((a, b) => a.length - b.length);
  return sorted.find((r) => len <= r.length) || sorted[sorted.length - 1];
}

export function getInternationalDocRules(ddi) {
  const ddiDigits = onlyDigits(ddi);
  return internationalDocByDDI[ddiDigits] || null;
}

export function maskInternationalDocument(value, ddi) {
  const ddiDigits = onlyDigits(ddi);
  // DDI 55 (Brasil): CNPJ pode ser alfanumérico — preservar letras maiúsculas
  const isBrazil = ddiDigits === '55';
  const normalized = isBrazil
    ? safeStr(value).toUpperCase().replace(/[^A-Z0-9]/g, '')
    : onlyDigits(value);
  const ruleSet = getInternationalDocRules(ddi);
  if (!ruleSet) return normalized.slice(0, 20);
  const rule = pickInternationalRule(normalized.length, ruleSet.rules);
  if (!rule) return normalized.slice(0, 20);
  const clipped = normalized.slice(0, rule.length);
  return applyMaskPattern(clipped, rule.mask);
}

export function getInternationalDocPlaceholder(ddi) {
  const ruleSet = getInternationalDocRules(ddi);
  const rule = pickInternationalRule(0, ruleSet?.rules || []);
  if (!rule?.mask) return 'Ex: Passaporte, DNI, etc.';
  return rule.mask.replace(/#/g, '0');
}

export function maskCPF(value) {
  const digits = onlyDigits(value).slice(0, 11);
  let out = digits;
  if (digits.length > 3) out = `${digits.slice(0, 3)}.${digits.slice(3)}`;
  if (digits.length > 6) out = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6)}`;
  if (digits.length > 9)
    out = `${digits.slice(0, 3)}.${digits.slice(3, 6)}.${digits.slice(6, 9)}-${digits.slice(9)}`;
  return out;
}

export function maskCNPJ(value) {
  const chars = safeStr(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);
  let out = chars;
  if (chars.length > 2) out = `${chars.slice(0, 2)}.${chars.slice(2)}`;
  if (chars.length > 5) out = `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5)}`;
  if (chars.length > 8)
    out = `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8)}`;
  if (chars.length > 12)
    out = `${chars.slice(0, 2)}.${chars.slice(2, 5)}.${chars.slice(5, 8)}/${chars.slice(8, 12)}-${chars.slice(12)}`;
  return out;
}

export function maskCpfCnpj(value) {
  const chars = safeStr(value)
    .toUpperCase()
    .replace(/[^A-Z0-9]/g, '')
    .slice(0, 14);
  const hasLetters = /[A-Z]/.test(chars);
  if (!hasLetters && chars.length <= 11) return maskCPF(chars);
  return maskCNPJ(chars);
}

export function maskPhone(value) {
  const digits = onlyDigits(value).slice(0, 11);
  if (digits.length === 0) return '';
  if (digits.length <= 2) return `(${digits}`;
  const ddd = digits.slice(0, 2);
  const rest = digits.slice(2);
  if (rest.length === 0) return `(${ddd})`;
  if (rest.length <= 4) return `(${ddd}) ${rest}`;
  if (rest.length <= 8) return `(${ddd}) ${rest.slice(0, 4)}-${rest.slice(4)}`;
  return `(${ddd}) ${rest.slice(0, 5)}-${rest.slice(5, 9)}`;
}

export function formatSouthAmericaPhone(value, defaultDDI = '55') {
  const digits = onlyDigits(value);

  const ddi = onlyDigits(defaultDDI) || '55';

  const ddd = digits.slice(0, 2);
  const num = digits.slice(2);

  if (ddd.length < 1) return '';

  if (num.length >= 9) return `${ddi} (${ddd}) ${num.slice(0, 5)}-${num.slice(5, 9)}`;
  if (num.length >= 8) return `${ddi} (${ddd}) ${num.slice(0, 4)}-${num.slice(4, 8)}`;

  return `${ddi} (${ddd}) ${num}`;
}

export function maskDDI(value) {
  const digits = safeStr(value).replace(/\D/g, '').slice(0, 3);
  return digits;
}
